// ** React Imports
import { useEffect, useMemo, useState } from "react";

// ** Redux & Store & Actions
import { connect, useSelector } from "react-redux";
import { register } from "store/modules/auth";

// ** Third Party Components
import { Wizard } from "react-use-wizard";
import loadable from "@loadable/component";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

// ** Custom Components
import Block from "components/layout/Block";
import GoogleLogoIcon from "containers/Oauth/icons/GoogleLogoIcon";
import TwitterLogoIcon from "containers/Oauth/icons/TwitterLogoIcon";
import Text from "components/typography/Text";
import Button from "components/base/Button";
import { GoogleOAuthButton } from "containers/Oauth/buttons/GoogleOAuthButton";
import { TwitterOAuthButton } from "containers/Oauth/buttons/TwitterOAuthButton";

//** Register Components
import StepGender from "containers/Landing/containers/Register/components/StepGender";

// ** Config
import configGlobal from "configuration";

// ** Helpers
import { trackOpenRegistrationForm } from "helpers/analytics";

const StepCoupleGender = loadable(
	() =>
		import(
			"containers/Landing/containers/Register/components/StepCoupleGender"
		),
	{ ssr: false }
);
const StepAge = loadable(
	() => import("containers/Landing/containers/Register/components/StepAge"),
	{ ssr: false }
);
const StepLogin = loadable(
	() => import("containers/Landing/containers/Register/components/StepLogin"),
	{
		ssr: false,
	}
);
const StepEmail = loadable(
	() => import("containers/Landing/containers/Register/components/StepEmail"),
	{
		ssr: false,
	}
);
const StepPassword = loadable(
	() =>
		import(
			"containers/Landing/containers/Register//components/StepPassword"
		),
	{
		ssr: false,
	}
);
const StepLocation = loadable(
	() =>
		import(
			"containers/Landing/containers/Register/components/StepLocation"
		),
	{
		ssr: false,
	}
);
const StepThankYou = loadable(
	() =>
		import(
			"containers/Landing/containers/Register/components/StepThankYou"
		),
	{
		ssr: false,
	}
);

const StyledCloseButton = styled(Button)`
	margin-left: auto;
	font-size: 1rem;
	text-shadow: 0 1px 0 #fff;
	color: rgba(255, 255, 255, 0.65);
	svg {
		color: rgba(255, 255, 255, 0.95);
	}
`;

// #####################################################

const Register = ({ coupleCase, currentStep, oAuth, setOAuth }) => {
	const { t } = useTranslation(["landing", "common"]);

	// Debug logging
	if (window.DEBUG) {
		// eslint-disable-next-line no-console
		console.log("Register - currentStep", currentStep);
		// eslint-disable-next-line no-console
		console.log("Register - oAuth", oAuth);
	}

	const [showCancelOAuthButton, setShowCancelOAuthButton] = useState(
		oAuth.active
	);
	const [showContinueWithOAuthButton, setShowContinueWithOAuthButton] =
		useState(true);

	const isClient = useSelector((state) => state.globalCommon.isClient);

	const registartionId = useSelector(
		(state) => state.auth.register.registerForm.registrationId
	);
	let firebaseHashObject = null;

	try {
		firebaseHashObject =
			isClient && localStorage
				? localStorage.getItem(
						configGlobal.firebase.localStorage.itemName
				  )
				: null;
	} catch (e) {
		firebaseHashObject = null;
	}

	// ** Przywracanie ostatnio aktywnego kroku z Redux store
	const savedStep = useMemo(() => {
		// Sprawdzamy czy currentStep jest w dozwolonym zakresie
		if (currentStep !== null && currentStep !== undefined) {
			const maxStep = !oAuth.active ? 7 : 6;
			if (currentStep >= 0 && currentStep <= maxStep) {
				return currentStep;
			}
		}
		return 0; // Domyślnie pierwszy krok
	}, [currentStep, oAuth.active]);

	// Debug logging
	if (window.DEBUG) {
		// eslint-disable-next-line no-console
		console.log("Register - savedStep", savedStep);
	}

	useEffect(() => {
		StepCoupleGender.preload();
		StepAge.preload();
		StepLogin.preload();
		StepPassword.preload();
		StepEmail.preload();
		StepLocation.preload();
		StepThankYou.preload();
	}, []);

	useEffect(() => {
		if (currentStep === null && !registartionId) {
			trackOpenRegistrationForm();
		}
	}, [currentStep, registartionId]);

	// #####################################################
	return (
		<Block>
			{oAuth.active && (
				<Block flex alignEnd relative>
					{oAuth.provider === "twitter" ? (
						<>
							<TwitterLogoIcon customMargin="0 0.5rem 0 0" />
							<Text mr={2}>
								{t("landing:registerForFreeWithTwitter")}
							</Text>
						</>
					) : (
						<>
							<GoogleLogoIcon customMargin="0 0.5rem 0 0" />
							<Text mr={2}>
								{t("landing:registerForFreeWithGoogle")}
							</Text>
						</>
					)}
					{showCancelOAuthButton && (
						<StyledCloseButton
							label={t("common:cancel")}
							onClick={() => {
								setOAuth({
									provider: null,
									email: null,
									token: null,
									active: false,
									loading: false,
									reset: true,
								});
							}}
						>
							<Text style={{ lineHeight: "21px" }}>
								{t("common:cancel")}
							</Text>
						</StyledCloseButton>
					)}
				</Block>
			)}

			<Wizard startIndex={savedStep}>
				{/* STEP 1 */}
				<StepGender
					currentStep={1}
					totalSteps={!oAuth.active ? 8 : 7}
					oAuth={oAuth}
					setOAuth={setOAuth}
					setShowContinueWithOAuthButton={
						setShowContinueWithOAuthButton
					}
				/>
				{/* STEP 2  */}
				{coupleCase ? (
					<StepCoupleGender
						currentStep={2}
						totalSteps={!oAuth.active ? 8 : 7}
						oAuth={oAuth}
						setOAuth={setOAuth}
						setShowContinueWithOAuthButton={
							setShowContinueWithOAuthButton
						}
					/>
				) : (
					<StepAge
						currentStep={2}
						totalSteps={!oAuth.active ? 8 : 7}
						oAuth={oAuth}
						setOAuth={setOAuth}
						setShowContinueWithOAuthButton={
							setShowContinueWithOAuthButton
						}
					/>
				)}
				{/* STEP 3 */}
				<StepLocation
					currentStep={3}
					totalSteps={!oAuth.active ? 8 : 7}
					oAuth={oAuth}
					setOAuth={setOAuth}
					setShowContinueWithOAuthButton={
						setShowContinueWithOAuthButton
					}
				/>
				{/* STEP 4 */}
				<StepLogin
					currentStep={4}
					totalSteps={!oAuth.active ? 8 : 7}
					oAuth={oAuth}
					setOAuth={setOAuth}
					setShowContinueWithOAuthButton={
						setShowContinueWithOAuthButton
					}
				/>

				{/* STEP 5 */}
				{!oAuth.active && (
					<StepPassword
						currentStep={5}
						totalSteps={8}
						oAuth={oAuth}
						setOAuth={setOAuth}
						setShowContinueWithOAuthButton={
							setShowContinueWithOAuthButton
						}
					/>
				)}
				{/* STEP 6 */}
				<StepEmail
					currentStep={!oAuth.active ? 6 : 5}
					totalSteps={!oAuth.active ? 8 : 7}
					oAuth={oAuth}
					setOAuth={setOAuth}
					setShowContinueWithOAuthButton={
						setShowContinueWithOAuthButton
					}
				/>
				{/* STEP 7 */}
				<StepThankYou
					currentStep={!oAuth.active ? 7 : 6}
					totalSteps={!oAuth.active ? 8 : 7}
					setShowCancelOAuthButton={setShowCancelOAuthButton}
					setOAuth={setOAuth}
					setShowContinueWithOAuthButton={
						setShowContinueWithOAuthButton
					}
				/>
			</Wizard>

			{showContinueWithOAuthButton && !oAuth.active && (
				<Block flex column mt={4} justifyEnd>
					<GoogleOAuthButton
						oAuth={oAuth}
						setOAuth={setOAuth}
						firebaseHashObject={firebaseHashObject}
					/>
					<TwitterOAuthButton
						oAuth={oAuth}
						setOAuth={setOAuth}
						firebaseHashObject={firebaseHashObject}
					/>
				</Block>
			)}
		</Block>
	);
};

// #####################################################

const mapStateToProps = ({
	auth: {
		register: {
			currentStep,
			registerForm: {
				gender: { coupleCase },
			},
		},
	},
}) => ({
	coupleCase,
	currentStep,
});

export default connect(mapStateToProps, {
	register,
})(Register);

// #####################################################
